import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

// Components
import { Button, Card, Empty, Icon, Input, Modal, Spin, Table, Tag } from 'antd';
import EditUser from './EditUser';

// Actions
import {
    fetchAdminUsers,
    handleAdminDeleteUser,
    handleAdminEditUser,
    handleAdminUndeleteUser,
    onSearchUsers,
} from '../../data/homes/actions';

// Selectors
import { getAllUsers, getAllUsersAreLoading, getSearchUserFields } from '../../data/homes/selectors';
import { isRoleAdministrator } from '../../data/auth/selectors';

// Others
const ButtonGroup = Button.Group;

class UsersTab extends PureComponent {
    state = {
        // Delete / undelete
        deleteModalShown: false,
        deleteModalIsLoading: false,
        recordToDelete: null,

        // Edit
        editModalShown: false,
        editModalIsLoading: false,
        recordToEdit: null,
    };

    componentWillMount() {
        const { fetchAdminUsers, searchUserFields } = this.props;
        const { searchName, searchEmail, searchCompany } = searchUserFields;
        if (searchName  || searchEmail || searchCompany) {
            this.handleSearch()
        } else {
            fetchAdminUsers();
        }
    }

    onDeleteModalShownToggle = (isShown, record) => () => {
        this.setState({ deleteModalShown: isShown, recordToDelete: record || null });
    };

    onEditModalShownToggle = (isShown, record) => () => {
        this.setState({ editModalShown: isShown, recordToEdit: record || null });
    };

    onEditModalStateReset = () => {
        this.setState(() => ({
            editModalShown: false,
            editModalIsLoading: false,
            recordToEdit: null
        }));
    };

    onDeleteModalStateReset = () => {
        this.setState(() => ({
            deleteModalShown: false,
            deleteModalIsLoading: false,
            recordToDelete: null
        }));
    };

    onEditUser = (values, id) => {
        this.setState({ editModalIsLoading: true });
        const { fetchAdminUsers, handleAdminEditUser } = this.props;
        return handleAdminEditUser(values, id).then(() => {
            this.onDeleteModalStateReset();
            fetchAdminUsers();
        });
    };

    onDeleteUser = () => {
        this.setState({ deleteModalIsLoading: true });

        const { recordToDelete } = this.state;
        const { params, fetchAdminUsers, handleAdminUndeleteUser, handleAdminDeleteUser } = this.props;
        if (recordToDelete.deleted) {
            handleAdminUndeleteUser(recordToDelete.id, params).then(() => {
                this.onDeleteModalStateReset();
                fetchAdminUsers();
            });
        } else {
            handleAdminDeleteUser(recordToDelete.id, params)
                .then(() => {
                    this.onDeleteModalStateReset();
                    fetchAdminUsers();
                })
                .catch(() => {
                    this.onDeleteModalStateReset();
                });
        }
    };

    handlePageChange = (page, pageSize) => {
        const { fetchAdminUsers, adminUsers, searchUserFields } = this.props;
        const { searchName, searchEmail, searchCompany } = searchUserFields;
        if (adminUsers && adminUsers.size > 0) {
            fetchAdminUsers(page - 1, pageSize, searchName, searchEmail, searchCompany);
        }
    };

    handleShowSizeChange = (page, pageSize) => {
        const { fetchAdminUsers, adminUsers } = this.props;
        if (adminUsers && adminUsers.size > 0) {
            fetchAdminUsers(page - 1, pageSize);
        }
    };

    handleSearch = () => {
        this.handlePageChange(1, 20);
    };

    render() {
        const { adminUsersAreLoading, adminUsers, msgs, ownerData, isAdmin, searchUserFields, onSearchUsers } = this.props;
        const {
            recordToDelete,
            deleteModalShown,
            deleteModalIsLoading,
            editModalShown,
            editModalIsLoading,
            recordToEdit,
        } = this.state;
        const { searchName, searchEmail, searchCompany } = searchUserFields;
        const columns = [
            {
                title: msgs.homes.detail.userSurname,
                dataIndex: 'surename',
                key: 'surename'
            },
            {
                title: msgs.homes.detail.userName,
                dataIndex: 'name',
                key: 'name'
            },
            {
                title: msgs.homes.detail.userEmail,
                dataIndex: 'email',
                key: 'email'
            },
            {
                title: msgs.users.permissionsLabel,
                dataIndex: 'admin',
                key: 'admin',
                render: admin => (
                    <span>
                        {admin ? (
                            <Tag color="volcano">{msgs.permissionsIsAdminTag}</Tag>
                        ) : (
                            <Tag color="green">{msgs.permissionsIsNotAdminTag}</Tag>
                        )}
                    </span>
                )
            },
            {
                title: msgs.users.permissionsLabel,
                dataIndex: 'tester',
                key: 'tester',
                render: tester => (
                    <span>
                        {tester ? (
                            <Tag color="yellow">{msgs.permissionsIsTesterTag}</Tag>
                        ) : (
                            <Tag color="green">{msgs.permissionsIsNotAdminTag}</Tag>
                        )}
                    </span>
                )
            },
            {
                title: msgs.users.permissionsLabel,
                dataIndex: 'installer',
                key: 'installer',
                render: installer => (
                    <span>
                        {installer ? (
                            <Tag color="blue">{msgs.permissionsIsInstallerTag}</Tag>
                        ) : (
                            <Tag color="green">{msgs.permissionsIsNotAdminTag}</Tag>
                        )}
                    </span>
                )
            },
            {
                title: msgs.actionsLabel,
                key: 'action',
                className: 'text-right',
                width: 200,
                render: (text, record) => (
                    <span style={{ float: 'right' }}>
                        {!record.deleted && (
                            <ButtonGroup style={{ marginRight: 10 }}>
                                <Button
                                    type="primary"
                                    onClick={this.onEditModalShownToggle(true, record)}
                                >
                                    <Icon type="edit" />
                                </Button>
                            </ButtonGroup>
                        )}
                        {record.deleted ? (
                            <ButtonGroup>
                                <Button
                                    type="danger"
                                    ghost
                                    onClick={this.onDeleteModalShownToggle(true, record)}
                                >
                                    <Icon type="undo" /> {msgs.undelete}
                                </Button>
                            </ButtonGroup>
                        ) : (
                            <ButtonGroup style={{ marginRight: 10 }}>
                                <Button
                                    type="danger"
                                    onClick={this.onDeleteModalShownToggle(true, record)}
                                >
                                    <Icon type="delete" />
                                </Button>
                            </ButtonGroup>
                        )}
                    </span>
                )
            }
        ];

        return [
            <Card
                loading={adminUsersAreLoading}
                key="users-table-card"
                bodyStyle={{ padding: 0 }}
                title={
                    !adminUsersAreLoading &&
                    ownerData &&
                    `${msgs.homes.detail.owner}: ${ownerData.get('name')} ${ownerData.get('surename')}`
                }
                extra={
                    !adminUsersAreLoading && [
                        isAdmin
                            ? <>
                                <span key="inp0">User search:</span>
                                <Input.Search key="inp1" placeholder="Name"
                                              style={{ width: '150px', margin: '0 8px'}} defaultValue={searchName}
                                              onChange={e => onSearchUsers({searchName: e.target.value, searchEmail, searchCompany: ''})}
                                              onSearch={this.handleSearch} />
                                <Input.Search key="inp2" placeholder="Email"
                                              style={{ width: '200px', margin: '0 8px'}} defaultValue={searchEmail}
                                              onChange={e => onSearchUsers({searchName, searchEmail: e.target.value, searchCompany: ''})}
                                              onSearch={this.handleSearch} />
                                <Input.Search key="inp3" placeholder="Company"
                                              style={{ width: '150px', margin: '0 8px'}} defaultValue={searchCompany}
                                              onChange={e => onSearchUsers({searchName: '', searchEmail: '', searchCompany: e.target.value})}
                                              onSearch={this.handleSearch} />
                            </>
                            : null
                    ]
                }
            >
                {adminUsers && adminUsers.size > 0 ? (
                    <Table
                        pagination={{
                            showSizeChanger: true,
                            defaultPageSize:
                                adminUsers && adminUsers.size > 0 ? adminUsers.get('size') : 20,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            pageSize: adminUsers && adminUsers.size > 0 ? adminUsers.get('size') : 20,
                            current:
                                adminUsers && adminUsers.size > 0 ? adminUsers.get('number') + 1 : 1,
                            total:
                                adminUsers && adminUsers.size > 0 ? adminUsers.get('totalElements') : 0,
                            onChange: this.handlePageChange,
                            onShowSizeChange: this.handleShowSizeChange
                        }}
                        rowClassName={record => {
                            if (record.deleted) {
                                return 'ant-table-deleted-row';
                            }
                        }}
                        dataSource={
                            adminUsers && adminUsers.size > 0 && adminUsers.get('content').size > 0
                                ? adminUsers.get('content').toJS()
                                : []
                        }
                        columns={columns}
                    />
                ) : (
                    <Empty
                        description={<span>{msgs.noDataAvailable}</span>}
                        style={{ margin: '30px auto' }}
                    />
                )}
            </Card>,
            recordToDelete && (
                <Modal
                    key="delete-user-modal"
                    visible={deleteModalShown}
                    onCancel={this.onDeleteModalStateReset}
                    onOk={this.onDeleteUser}
                    okText={recordToDelete.deleted ? msgs.undelete : msgs.delete}
                    okType={recordToDelete.deleted ? 'primary' : 'danger'}
                    confirmLoading={deleteModalIsLoading}
                    cancelText={msgs.close}
                    title={recordToDelete.deleted ? msgs.sureToUndeleteTitle : msgs.sureToDeleteTitle}
                >
                    <Spin spinning={deleteModalIsLoading} size="large">
                        <div>
                            <p>
                                {recordToDelete.deleted
                                    ? msgs.administration.sureToUndeleteUserContent
                                    : msgs.administration.sureToDeleteUserContent}
                                <strong>
                                    {recordToDelete.name} {recordToDelete.surename}
                                </strong>
                                ?
                            </p>
                        </div>
                    </Spin>
                </Modal>
            ),

            recordToEdit && (
                <EditUser
                    key="edit-user-modal"
                    msgs={msgs}
                    onSubmit={this.onEditUser}
                    isShown={editModalShown}
                    isLoading={editModalIsLoading}
                    selectedUser={recordToEdit}
                    onClose={this.onEditModalStateReset}
                />
            )
        ];
    }
}

export default connect(
    (state, ownProps) => ({
        ...ownProps,
        adminUsers: getAllUsers(state),
        adminUsersAreLoading: getAllUsersAreLoading(state),
        isAdmin: isRoleAdministrator(state),
        searchUserFields: getSearchUserFields(state),
    }),
    {
        fetchAdminUsers,
        handleAdminDeleteUser,
        handleAdminEditUser,
        handleAdminUndeleteUser,
        onSearchUsers,
    }
)(UsersTab);
